import React from 'react';
import { Box, Icon, keyframes } from '@chakra-ui/react';
import { FaStar } from 'react-icons/fa';

// Define the spin animation keyframes
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Pulse glow effect to make it more noticeable
const pulseGlow = keyframes`
  0%, 100% { 
    filter: drop-shadow(0 0 3px rgba(251, 191, 36, 0.8));
    transform: rotate(0deg) scale(1);
  }
  50% { 
    filter: drop-shadow(0 0 8px rgba(251, 191, 36, 1));
    transform: rotate(180deg) scale(1.1);
  }
`;

/**
 * FavoriteStarIndicator - An animated spinning star for favorited businesses
 * 
 * @param {Object} props
 * @param {number} props.size - Size in pixels (default: 16)
 * @param {string} props.color - Star color (default: yellow.400)
 * @param {number} props.duration - Animation duration in seconds (default: 3)
 * @param {Object} props.style - Additional inline styles
 * @param {Object} props.boxProps - Additional Box props
 */
const FavoriteStarIndicator = ({ 
  size = 16, 
  color = 'yellow.400',
  duration = 3,
  style = {},
  ...boxProps 
}) => {
  const animation = `${spin} ${duration}s linear infinite`;
  
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      {...boxProps}
    >
      <Icon
        as={FaStar}
        boxSize={`${size}px`}
        color={color}
        animation={animation}
        style={{
          filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.8))',
          ...style
        }}
      />
    </Box>
  );
};

/**
 * FavoriteStarLabel - A custom label component for Recharts bars
 * Use this with the Bar component's label prop to show stars above favorited businesses
 * 
 * @param {Object} props - Recharts label props
 * @param {Object} props.favoriteMap - Map of short names to maps_uri
 * @param {Array} props.favoriteUris - Array of favorited maps_uri values
 */
export const FavoriteStarLabel = ({ 
  x, 
  y, 
  width, 
  value, 
  name,
  payload,
  favoriteMap = {},
  favoriteUris = [],
  isFavoriteCheck = null
}) => {
  // Check if this business is favorited
  let isFavorited = false;
  
  if (isFavoriteCheck && typeof isFavoriteCheck === 'function') {
    // Use custom check function if provided
    isFavorited = isFavoriteCheck(name);
  } else {
    // Prefer direct maps_uri on the datum payload (most reliable)
    const payloadMapsUri = payload?.maps_uri || payload?.mapsUri;
    const mapsUri = payloadMapsUri || favoriteMap[name];
    isFavorited = Boolean(mapsUri && favoriteUris.includes(mapsUri));
  }
  
  if (!isFavorited) return null;
  
  // Position the star above the bar, centered horizontally
  const starSize = 18;
  const starX = x + (width / 2) - (starSize / 2);
  const starY = y - starSize - 4; // 4px gap above the bar
  
  return (
    <g>
      <foreignObject
        x={starX}
        y={starY}
        width={starSize + 4}
        height={starSize + 4}
        style={{ overflow: 'visible' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          <svg
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            style={{
              animation: `favoriteStarSpin 3s linear infinite`,
              filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.9))'
            }}
          >
            <defs>
              <style>
                {`
                  @keyframes favoriteStarSpin {
                    from { transform: rotate(0deg); transform-origin: center; }
                    to { transform: rotate(360deg); transform-origin: center; }
                  }
                `}
              </style>
              <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F6E05E" />
                <stop offset="50%" stopColor="#ECC94B" />
                <stop offset="100%" stopColor="#D69E2E" />
              </linearGradient>
            </defs>
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              fill="url(#starGradient)"
              stroke="#D69E2E"
              strokeWidth="0.5"
            />
          </svg>
        </div>
      </foreignObject>
    </g>
  );
};

/**
 * Renders enhanced 3D gold star SVG defs (gradients and filters)
 * Call this once per chart to define reusable gradients and filters
 * 
 * @param {string} uniqueId - Unique identifier for this star instance
 * @returns {JSX.Element} SVG defs element with gradients and filters
 */
export const Enhanced3DStarDefs = ({ uniqueId }) => (
  <defs>
    {/* Enhanced 3D gold gradient */}
    <linearGradient id={`${uniqueId}-gradient`} x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stopColor="#FFD700" />
      <stop offset="30%" stopColor="#FFC700" />
      <stop offset="60%" stopColor="#FFB700" />
      <stop offset="100%" stopColor="#D4AF37" />
    </linearGradient>
    {/* Inner highlight for 3D effect */}
    <radialGradient id={`${uniqueId}-highlight`}>
      <stop offset="0%" stopColor="#FFFACD" stopOpacity="0.8" />
      <stop offset="50%" stopColor="#FFD700" stopOpacity="0.4" />
      <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
    </radialGradient>
    {/* Enhanced glow filter */}
    <filter id={`${uniqueId}-glow`} x="-100%" y="-100%" width="300%" height="300%">
      <feGaussianBlur stdDeviation="2" result="blur" />
      <feFlood floodColor="#FFD700" floodOpacity="0.8" result="color" />
      <feComposite in="color" in2="blur" operator="in" result="shadow" />
      <feMerge>
        <feMergeNode in="shadow" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
    {/* Shadow for 3D depth */}
    <filter id={`${uniqueId}-shadow`}>
      <feGaussianBlur in="SourceAlpha" stdDeviation="1" />
      <feOffset dx="1" dy="2" result="offsetblur" />
      <feComponentTransfer>
        <feFuncA type="linear" slope="0.3" />
      </feComponentTransfer>
      <feMerge>
        <feMergeNode />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);

/**
 * Renders the enhanced 3D gold star path with highlight
 * 
 * @param {Object} options
 * @param {string} options.uniqueId - Unique identifier matching the defs
 * @param {number} options.starSize - Size of the star (default: 18)
 * @returns {JSX.Element} SVG star path elements
 */
export const Enhanced3DStarPath = ({ uniqueId, starSize = 18 }) => (
  <>
    {/* Main star body with 3D gradient */}
    <path
      d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
      fill={`url(#${uniqueId}-gradient)`}
      stroke="#B8860B"
      strokeWidth="0.8"
      filter={`url(#${uniqueId}-glow)`}
    />
    {/* Inner highlight for 3D effect */}
    <ellipse
      cx={starSize/2}
      cy={starSize*0.35}
      rx={starSize*0.15}
      ry={starSize*0.12}
      fill={`url(#${uniqueId}-highlight)`}
      opacity="0.6"
    />
  </>
);

/**
 * Creates a custom label renderer function for Recharts bars
 * This function returns a label component that shows spinning stars above favorited businesses
 * 
 * @param {Object} options
 * @param {Array} options.data - The chart data array with full business data
 * @param {Array} options.favoriteUris - Array of favorited maps_uri values
 * @param {string} options.nameKey - Key to use for matching (default: 'name')
 * @returns {Function} Label renderer function for Recharts Bar component
 */
export const createFavoriteStarLabelRenderer = ({ 
  data = [], 
  favoriteUris = [],
  nameKey = 'name'
}) => {
  // Create a map from short display name to maps_uri
  const favoriteMap = {};
  data.forEach(item => {
    if (item.maps_uri) {
      const shortName = item[nameKey]?.split(' ').slice(0, 2).join(' ') || item[nameKey];
      favoriteMap[shortName] = item.maps_uri;
    }
  });
  
  return (props) => (
    <FavoriteStarLabel
      {...props}
      favoriteMap={favoriteMap}
      favoriteUris={favoriteUris}
    />
  );
};

/**
 * SVG-based spinning star for use directly in Recharts
 * This is a simpler version that can be rendered inline in SVG
 */
export const SpinningStar = ({ cx, cy, size = 16 }) => {
  return (
    <g transform={`translate(${cx - size/2}, ${cy - size/2})`}>
      <defs>
        <style>
          {`
            @keyframes favoriteStarRotate {
              from { transform: rotate(0deg); transform-origin: ${size/2}px ${size/2}px; }
              to { transform: rotate(360deg); transform-origin: ${size/2}px ${size/2}px; }
            }
            .spinning-star {
              animation: favoriteStarRotate 3s linear infinite;
            }
          `}
        </style>
        <linearGradient id="spinningStarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F6E05E" />
          <stop offset="50%" stopColor="#ECC94B" />
          <stop offset="100%" stopColor="#D69E2E" />
        </linearGradient>
        <filter id="starGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feFlood floodColor="#ECC94B" floodOpacity="0.6" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="shadow" />
          <feMerge>
            <feMergeNode in="shadow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g className="spinning-star" filter="url(#starGlow)">
        <path
          d={`M${size/2} 0 L${size*0.63} ${size*0.38} L${size} ${size*0.38} L${size*0.69} ${size*0.62} L${size*0.81} ${size} L${size/2} ${size*0.75} L${size*0.19} ${size} L${size*0.31} ${size*0.62} L0 ${size*0.38} L${size*0.37} ${size*0.38} Z`}
          fill="url(#spinningStarGradient)"
          stroke="#D69E2E"
          strokeWidth="0.5"
        />
      </g>
    </g>
  );
};

/**
 * Creates a custom label renderer for Recharts Pie charts
 * Renders the standard label text plus a spinning star for favorited businesses
 * The star is rendered AFTER (on top of) the text to ensure it's visible
 * 
 * @param {Object} options
 * @param {Object} options.businessNameToUriMap - Map of short business names to maps_uri
 * @param {Array} options.favoriteUris - Array of favorited maps_uri values
 * @returns {Function} Label renderer function for Recharts Pie component
 */
export const createFavoriteStarPieLabelRenderer = ({
  businessNameToUriMap = {},
  favoriteUris = []
}) => {
  return (props) => {
    const { cx, cy, midAngle, outerRadius, name, percent } = props;
    const RADIAN = Math.PI / 180;
    
    // Calculate label position (same as default Recharts behavior)
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Check if this business is favorited
    // Prefer direct maps_uri on the datum payload when available.
    const payloadMapsUri = props?.payload?.maps_uri || props?.payload?.mapsUri;
    const mapsUriFromMap = businessNameToUriMap[name];
    const mapsUri = payloadMapsUri || mapsUriFromMap;
    const isFavorited = Boolean(mapsUri && favoriteUris?.includes(mapsUri));
    
    const labelText = `${name} (${(percent * 100).toFixed(1)}%)`;
    const textAnchor = x > cx ? 'start' : 'end';
    
    // Star configuration - increased size for better visibility
    const starSize = 18;
    const starOffset = x > cx ? -starSize - 4 : 4; // Position star before text on left side, after on right side
    const starX = x > cx ? x + starOffset : x + starOffset;
    
    return (
      <g style={{ pointerEvents: 'none' }}>
        {/* Render the text label */}
        <text
          x={x}
          y={y}
          fill="#2D3748"
          textAnchor={textAnchor}
          dominantBaseline="central"
          fontSize={14}
          fontWeight="600"
          fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"
        >
          {labelText}
        </text>
        
        {/* Render spinning star for favorited businesses - rendered LAST to be on TOP */}
        {isFavorited && (
          <g transform={`translate(${starX}, ${y - starSize / 2})`} style={{ zIndex: 999999 }}>
            <g>
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from={`0 ${starSize/2} ${starSize/2}`}
                to={`360 ${starSize/2} ${starSize/2}`}
                dur="3s"
                repeatCount="indefinite"
              />
              {/* Shadow layer for depth */}
              <path
                d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
                fill="#8B7355"
                fillOpacity="0.3"
              />
              {/* Base dark gold layer */}
              <path
                d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
                fill="#D4AF37"
                stroke="#B8860B"
                strokeWidth="1"
              />
              {/* Bright gold overlay */}
              <path
                d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
                fill="#FFD700"
                fillOpacity="0.7"
              />
              {/* Top highlight for 3D shine */}
              <ellipse
                cx={starSize/2}
                cy={starSize*0.3}
                rx={starSize*0.18}
                ry={starSize*0.14}
                fill="#FFFACD"
                fillOpacity="0.8"
              />
            </g>
          </g>
        )}
      </g>
    );
  };
};

export default FavoriteStarIndicator;
